
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { DeleteCampaignResult } from "./types";

export const useDeleteCampaign = (userId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (campaignId: string): Promise<void> => {
      console.log('🗑️ useDeleteCampaign: Iniciando exclusão da campanha:', campaignId);
      console.log('👤 useDeleteCampaign: User ID:', userId);
      
      if (!campaignId) {
        throw new Error('ID da campanha é obrigatório');
      }
      
      try {
        // Primeiro, tentar usar a função de validação do banco
        console.log('📞 useDeleteCampaign: Chamando função delete_campaign_with_validation...');
        const { data, error } = await supabase.rpc('delete_campaign_with_validation', {
          campaign_id_param: campaignId
        });

        console.log('📊 useDeleteCampaign: Resposta da função:', { data, error });

        if (error) {
          console.error('❌ useDeleteCampaign: Erro na função do banco:', error);
          throw new Error(`Erro na função do banco: ${error.message}`);
        }

        // Fazer type assertion para o tipo correto
        const result = data as unknown as DeleteCampaignResult;
        console.log('🔍 useDeleteCampaign: Resultado parseado:', result);

        if (!result.success) {
          console.error('❌ useDeleteCampaign: Função retornou erro:', result.error);
          console.log('🐛 useDeleteCampaign: Debug info:', result.debug);
          
          // Se a função falhou, tentar exclusão direta como fallback
          console.log('🔄 useDeleteCampaign: Tentando exclusão direta como fallback...');
          const { error: directDeleteError } = await supabase
            .from('campaigns')
            .delete()
            .eq('id', campaignId)
            .eq('user_id', userId);

          if (directDeleteError) {
            console.error('❌ useDeleteCampaign: Erro na exclusão direta:', directDeleteError);
            throw new Error(result.error || 'Erro ao excluir campanha');
          }
          
          console.log('✅ useDeleteCampaign: Exclusão direta bem-sucedida');
        } else {
          console.log('✅ useDeleteCampaign: Exclusão via função bem-sucedida');
        }

        // Verificar se a campanha ainda existe após exclusão
        console.log('🔍 useDeleteCampaign: Verificando se a campanha ainda existe...');
        const { data: checkData, error: checkError } = await supabase
          .from('campaigns')
          .select('id')
          .eq('id', campaignId)
          .maybeSingle();

        if (checkError) {
          console.error('❌ useDeleteCampaign: Erro ao verificar exclusão:', checkError);
        } else if (checkData) {
          console.error('⚠️ useDeleteCampaign: Campanha ainda existe após exclusão:', checkData);
          throw new Error('Campanha não foi excluída do banco de dados');
        } else {
          console.log('✅ useDeleteCampaign: Confirmado - campanha foi excluída do banco');
        }

      } catch (error: any) {
        console.error('💥 useDeleteCampaign: Erro geral na exclusão:', error);
        throw error;
      }
    },
    onSuccess: (_, campaignId) => {
      console.log('🎉 useDeleteCampaign: Mutation onSuccess executada para campanha:', campaignId);
      
      // Invalidar e refetch imediato das queries
      console.log('🔄 useDeleteCampaign: Invalidando cache do React Query...');
      queryClient.invalidateQueries({ queryKey: ['campaigns', userId] });
      
      // Forçar refetch imediato
      console.log('🔄 useDeleteCampaign: Forçando refetch imediato...');
      queryClient.refetchQueries({ queryKey: ['campaigns', userId] });
      
      // Remover campanha específica do cache se existir
      console.log('🗑️ useDeleteCampaign: Removendo dados específicos do cache...');
      queryClient.removeQueries({ queryKey: ['campaigns', userId] });
      
      toast.success("Campanha excluída com sucesso");
      console.log('✅ useDeleteCampaign: Processo de exclusão concluído com sucesso');
    },
    onError: (error: any, campaignId) => {
      console.error('❌ useDeleteCampaign: Mutation onError executada:', error);
      console.error('🆔 useDeleteCampaign: ID da campanha com erro:', campaignId);
      
      const message = error.message || "Erro ao excluir campanha";
      toast.error(message);
      
      // Ainda assim tentar invalidar o cache para atualizar a UI
      console.log('🔄 useDeleteCampaign: Invalidando cache mesmo com erro...');
      queryClient.invalidateQueries({ queryKey: ['campaigns', userId] });
    },
  });
};
